import { useMemo, useState } from "react";
import {
  Download,
  Loader2,
  Eye,
  Check,
  AlertCircle,
  RefreshCw,
  FileUp,
} from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { Card } from "../app/components/primitives/card";
import { Button } from "../app/components/primitives/button";
import { Textarea } from "../app/components/primitives/textarea";
import { Badge } from "../app/components/primitives/badge";

// Types re-declared locally to avoid importing from the api/ resource module,
// which eagerly evaluates client.ts and throws if VITE_API_BASE_URL is unset
// (breaks Storybook tests).  The DataAdapter interface references the same
// shape via lazy import("...") types, so these stay in sync at typecheck time.
interface BulkSectionCounts {
  created: number;
  updated: number;
  skipped: number;
  collisions: number;
}

interface BulkPlanEntry {
  kind: "signal" | "alert" | "opportunity";
  name: string;
  action: "create" | "apply" | "no-op" | "collision" | "gap";
  detail: string;
}

interface BulkErrorEntry {
  kind: "yaml" | "signal" | "alert" | "opportunity";
  name: string;
  message: string;
}

interface BulkApplyOutcome {
  ok: boolean;
  dry_run: boolean;
  signals: BulkSectionCounts;
  alerts: BulkSectionCounts;
  opportunities: BulkSectionCounts;
  plan: BulkPlanEntry[];
  errors: BulkErrorEntry[];
}

const BULK_EXAMPLE_SLUGS = [
  { slug: "account-health", label: "Account-health signals" },
  { slug: "customer-alerts", label: "Customer-grain alerts & opportunities" },
  { slug: "estimate-alerts", label: "Estimate-grain alerts & opportunities" },
  {
    slug: "strategic-intelligence-opportunities",
    label: "Strategic-intelligence alerts & opportunities",
  },
] as const;

type Phase =
  | { kind: "idle" }
  | { kind: "previewing" }
  | { kind: "previewed"; outcome: BulkApplyOutcome; yamlAtPreview: string }
  | { kind: "applying"; yamlAtPreview: string }
  | { kind: "applied"; outcome: BulkApplyOutcome };

function totalChanges(outcome: BulkApplyOutcome): number {
  return (
    outcome.signals.created +
    outcome.signals.updated +
    outcome.alerts.created +
    outcome.alerts.updated +
    outcome.opportunities.created +
    outcome.opportunities.updated
  );
}

function totalCollisions(outcome: BulkApplyOutcome): number {
  return (
    outcome.signals.collisions +
    outcome.alerts.collisions +
    outcome.opportunities.collisions
  );
}

export function SettingsBulkDefinitionsPage() {
  const adapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const [yaml, setYaml] = useState("");
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [transportError, setTransportError] = useState<string | null>(null);

  // The freshly-pasted YAML must match what the user previewed before the
  // Apply button is allowed to fire — this is the preview-then-apply
  // enforcement the action layer can't enforce on its own.
  const previewIsCurrent = useMemo(() => {
    if (phase.kind !== "previewed") return false;
    return phase.yamlAtPreview === yaml;
  }, [phase, yaml]);

  const canPreview =
    yaml.trim().length > 0 &&
    phase.kind !== "previewing" &&
    phase.kind !== "applying";
  // Collisions block Apply: an entry whose version number already exists with
  // different content (usually an in-app edit) must be resolved — bump the
  // version in the YAML — before the batch can be applied from here.
  const canApply =
    phase.kind === "previewed" &&
    previewIsCurrent &&
    phase.outcome.ok &&
    phase.outcome.errors.length === 0 &&
    totalCollisions(phase.outcome) === 0 &&
    totalChanges(phase.outcome) > 0;

  function resetPhase() {
    setPhase({ kind: "idle" });
    setTransportError(null);
  }

  async function handlePreview() {
    setPhase({ kind: "previewing" });
    setTransportError(null);
    try {
      const outcome = await adapter.previewBulkDefinitions(yaml);
      setPhase({ kind: "previewed", outcome, yamlAtPreview: yaml });
    } catch (exc) {
      setTransportError(exc instanceof Error ? exc.message : String(exc));
      setPhase({ kind: "idle" });
    }
  }

  async function handleApply() {
    if (phase.kind !== "previewed" || !previewIsCurrent) return;
    setPhase({ kind: "applying", yamlAtPreview: phase.yamlAtPreview });
    setTransportError(null);
    try {
      const outcome = await adapter.applyBulkDefinitions(yaml);
      setPhase({ kind: "applied", outcome });
    } catch (exc) {
      setTransportError(exc instanceof Error ? exc.message : String(exc));
      setPhase({
        kind: "previewed",
        outcome:
          phase.kind === "previewed"
            ? phase.outcome
            : { ...emptyOutcome(false) },
        yamlAtPreview: phase.yamlAtPreview,
      });
    }
  }

  async function handleDownloadExample(slug: string) {
    try {
      const file = await adapter.getBulkExampleDownload(slug);
      capabilities.downloadTextFile(
        file.filename,
        file.content,
        "application/x-yaml",
      );
    } catch (exc) {
      setTransportError(exc instanceof Error ? exc.message : String(exc));
    }
  }

  async function handlePopulateFromExample(slug: string) {
    try {
      const file = await adapter.getBulkExampleDownload(slug);
      handleYamlChange(file.content);
    } catch (exc) {
      setTransportError(exc instanceof Error ? exc.message : String(exc));
    }
  }

  function handleYamlChange(next: string) {
    setYaml(next);
    // Editing after a preview invalidates it — go back to idle so Apply
    // can't fire on a stale preview.
    if (phase.kind === "previewed" || phase.kind === "applied") {
      setPhase({ kind: "idle" });
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Bulk import definitions</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Paste a YAML body in the <code>bulk_seed_definitions</code> format to
          create or update signal, alert, and opportunity definitions in one
          batch. Every entry carries an integer <code>version</code>; bump it to
          apply a change. Preview first to see what will change, then apply. The
          whole batch rolls back if any entry fails.
        </p>
      </div>

      <Card className="p-4">
        <div className="text-sm font-medium mb-2">Examples</div>
        <p className="text-xs text-muted-foreground mb-3">
          Load an example directly into the editor, or download the YAML file.
        </p>
        <div className="space-y-2">
          {BULK_EXAMPLE_SLUGS.map((example) => (
            <div key={example.slug} className="flex items-center gap-2">
              <span className="text-sm min-w-[200px]">{example.label}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePopulateFromExample(example.slug)}
              >
                <FileUp className="w-3.5 h-3.5 mr-1.5" />
                Load example
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownloadExample(example.slug)}
                title={`Download ${example.label} YAML`}
              >
                <Download className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-4">
        <label htmlFor="bulk-yaml" className="text-sm font-medium block mb-2">
          YAML body
        </label>
        <Textarea
          id="bulk-yaml"
          value={yaml}
          onChange={(e) => handleYamlChange(e.target.value)}
          placeholder="signals:&#10;  - name: ...&#10;alerts:&#10;  - name: ...&#10;opportunities:&#10;  - name: ..."
          className="font-mono text-xs min-h-[280px]"
          spellCheck={false}
        />
        <div className="flex items-center justify-end mt-3 gap-4 flex-wrap">
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handlePreview}
              disabled={!canPreview}
            >
              {phase.kind === "previewing" ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Eye className="w-4 h-4 mr-2" />
              )}
              Preview
            </Button>
            <Button onClick={handleApply} disabled={!canApply}>
              {phase.kind === "applying" ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Apply
            </Button>
          </div>
        </div>
      </Card>

      {transportError && (
        <Card className="p-4 border-destructive">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-sm">Request failed</div>
              <div className="text-sm text-muted-foreground mt-1">
                {transportError}
              </div>
            </div>
          </div>
        </Card>
      )}

      {(phase.kind === "previewed" || phase.kind === "applied") && (
        <OutcomePanel
          outcome={phase.outcome}
          appliedAlready={phase.kind === "applied"}
          previewStale={phase.kind === "previewed" && !previewIsCurrent}
          onReset={resetPhase}
        />
      )}
    </div>
  );
}

function OutcomePanel({
  outcome,
  appliedAlready,
  previewStale,
  onReset,
}: {
  outcome: BulkApplyOutcome;
  appliedAlready: boolean;
  previewStale: boolean;
  onReset: () => void;
}) {
  const heading = appliedAlready
    ? "Applied"
    : previewStale
      ? "Preview out of date"
      : "Preview";
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-medium">{heading}</h2>
          {outcome.ok ? (
            <Badge variant="default">OK</Badge>
          ) : (
            <Badge variant="destructive">Errors</Badge>
          )}
          {outcome.dry_run && !appliedAlready && (
            <Badge variant="outline">Dry run</Badge>
          )}
        </div>
        {appliedAlready && (
          <Button variant="ghost" size="sm" onClick={onReset}>
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Start another
          </Button>
        )}
      </div>

      {previewStale && (
        <div className="text-sm text-muted-foreground mb-3 p-2 rounded bg-muted">
          The YAML has changed since this preview ran. Preview again before
          applying.
        </div>
      )}

      {!appliedAlready && totalCollisions(outcome) > 0 && (
        <div className="text-sm mb-3 p-2 rounded border border-amber-500 bg-amber-50 text-amber-900">
          {totalCollisions(outcome)} collision(s): the version number already
          exists with different content (usually an in-app edit). Bump the{" "}
          <code>version</code> in the YAML for those entries to apply your
          change. Apply is blocked until they're resolved.
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        <SectionCounts label="Signals" counts={outcome.signals} />
        <SectionCounts label="Alerts" counts={outcome.alerts} />
        <SectionCounts label="Opportunities" counts={outcome.opportunities} />
      </div>

      {outcome.errors.length > 0 && (
        <div className="mt-4">
          <div className="text-sm font-medium mb-2">Errors</div>
          <ul className="space-y-1.5 text-sm">
            {outcome.errors.map((err, i) => (
              <li key={i} className="flex items-start gap-2">
                <AlertCircle className="w-3.5 h-3.5 text-destructive shrink-0 mt-0.5" />
                <span>
                  <span className="font-mono text-xs text-muted-foreground">
                    {err.kind}
                  </span>
                  {err.name && <span className="font-medium"> {err.name}</span>}
                  <span className="text-muted-foreground">
                    {" "}
                    — {err.message}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {outcome.plan.length > 0 && (
        <details className="mt-4">
          <summary className="text-sm font-medium cursor-pointer">
            Per-entry plan ({outcome.plan.length})
          </summary>
          <ul className="mt-2 space-y-1 text-sm">
            {outcome.plan.map((entry, i) => {
              const isProblem =
                entry.action === "collision" || entry.action === "gap";
              return (
                <li key={i} className="flex items-start gap-2">
                  <Badge
                    variant={isProblem ? "destructive" : "outline"}
                    className="text-xs shrink-0"
                  >
                    {entry.action}
                  </Badge>
                  <span className="font-mono text-xs text-muted-foreground shrink-0">
                    {entry.kind}
                  </span>
                  <span>
                    {entry.name}
                    {entry.detail && (
                      <span className="text-muted-foreground">
                        {" "}
                        — {entry.detail}
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </details>
      )}
    </Card>
  );
}

function SectionCounts({
  label,
  counts,
}: {
  label: string;
  counts: {
    created: number;
    updated: number;
    skipped: number;
    collisions: number;
  };
}) {
  return (
    <div className="rounded border border-border p-3">
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className="flex flex-wrap gap-2 text-sm">
        <span>
          <strong>{counts.created}</strong> create
        </span>
        <span>
          <strong>{counts.updated}</strong> apply
        </span>
        <span>
          <strong>{counts.skipped}</strong> no-op
        </span>
        {counts.collisions > 0 && (
          <span className="text-amber-700">
            <strong>{counts.collisions}</strong> collision
          </span>
        )}
      </div>
    </div>
  );
}

function emptyOutcome(dryRun: boolean): BulkApplyOutcome {
  return {
    ok: true,
    dry_run: dryRun,
    signals: { created: 0, updated: 0, skipped: 0, collisions: 0 },
    alerts: { created: 0, updated: 0, skipped: 0, collisions: 0 },
    opportunities: { created: 0, updated: 0, skipped: 0, collisions: 0 },
    plan: [],
    errors: [],
  };
}
